import * as THREE from "three";
import { Object3D, PerspectiveCamera, Scene, WebGLRenderTarget } from "three";
import { MainScene } from "../../MainCanvas/MainScene";
import { killRenderer } from "../../../utils/ThreeScene/resourcesCleaner";
import { createRenderer } from "../../../utils/ThreeScene/createRenderer";
import { SideType, SideTypeWithoutTop, StoreWallInterface } from "../../../store/types/store";
import { ScreenshotCanvasView } from "../types";
import { getWallLineVertices } from "../../SceneObjects/ButtonsScene/utils";
import { getCameraPositionToMiddleLine, getTargetPositionToMiddleLine } from "../../../store/helpers";
import { fitCameraToRoom } from "../../SmartOrbitControls/fitCameraToRoom";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass";
import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader";
import rotatePointAroundPoint from "../../../utils/rotatePointAroundPoint";

export const changeVisibleRacksOnView = (scene: Scene, side: SideTypeWithoutTop, view: ScreenshotCanvasView) => {
    scene.traverse((l: Object3D) => {
        if (side === "left" && view === "front" && (l.name === "rightRacks" || l.name === "rightDecor")) {
            l.visible = false;
        }
        if (side === "left" && view === "side" && (l.name === "frontRacks" || l.name === "frontDecor")) {
            l.visible = false;
        }
        if (side === "right" && view === "front" && (l.name === "leftRacks" || l.name === "leftDecor")) {
            l.visible = false;
        }
        if (side === "right" && view === "side" && (l.name === "backRacks" || l.name === "backDecor")) {
            l.visible = false;
        }
        if (side === "front" && view === "front" && (l.name === "backRacks" || l.name === "backDecor")) {
            l.visible = false;
        }
        if (side === "front" && view === "side" && (l.name === "rightRacks" || l.name === "rightDecor")) {
            l.visible = false;
        }
        if (side === "back" && view === "front" && (l.name === "frontRacks" || l.name === "frontDecor")) {
            l.visible = false;
        }
        if (side === "back" && view === "side" && (l.name === "leftRacks" || l.name === "leftDecor")) {
            l.visible = false;
        }
    });
};

export const getImageScene = (
    shelving: StoreWallInterface,
    side: SideTypeWithoutTop,
    view: ScreenshotCanvasView
): string => {
    const canvas = document.createElement("CANVAS") as HTMLCanvasElement;
    const width = 1971;
    const height = 1021;

    const renderer = createRenderer(canvas);

    const camera = new PerspectiveCamera(45, width / height, 0.0001, 200);
    canvas.width = width;
    canvas.height = height;

    const lineVertices = getWallLineVertices(side as SideType);
    const cameraPosition = getCameraPositionToMiddleLine(lineVertices, side, "item");
    const targetPosition = getTargetPositionToMiddleLine(lineVertices);
    const correctedPosition = fitCameraToRoom(targetPosition, cameraPosition, side as SideTypeWithoutTop, camera);
    const toCameraPosition = {
        x: correctedPosition.x,
        y: correctedPosition.y,
        z: correctedPosition.z,
    };

    if (view === "front") {
        camera.zoom = camera.zoom + 1;
        camera.position.set(toCameraPosition.x, toCameraPosition.y, toCameraPosition.z);
    } else {
        camera.zoom = camera.zoom + 0.5;
        const newCameraPosition = rotatePointAroundPoint(correctedPosition, targetPosition, 60);
        camera.position.set(newCameraPosition.x, newCameraPosition.y, newCameraPosition.z);
    }

    camera.lookAt(targetPosition);
    camera.updateProjectionMatrix();

    const size = renderer.getSize(new THREE.Vector2());
    renderer.setPixelRatio(1);
    const _pixelRatio = renderer.getPixelRatio();

    const _width = width;
    const _height = height;
    const renderTarget = new WebGLRenderTarget(_width * _pixelRatio, _height * _pixelRatio, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        depthBuffer: true,
        stencilBuffer: false,
    });
    renderTarget.texture.name = "EffectComposer.rt1";

    const COMPOSER = new EffectComposer(renderer, renderTarget);

    const sceneWithoutOneSide = MainScene.scene.clone();
    changeVisibleRacksOnView(sceneWithoutOneSide, side, view);

    const renderPass = new RenderPass(sceneWithoutOneSide, camera);
    renderPass.setSize(size.width, size.height);
    COMPOSER.addPass(renderPass);

    const fxaaPass = new ShaderPass(FXAAShader);
    fxaaPass.material.uniforms["resolution"].value.x = 1 / (width * _pixelRatio);
    fxaaPass.material.uniforms["resolution"].value.y = 1 / (height * _pixelRatio);
    fxaaPass.renderToScreen = true;

    COMPOSER.addPass(fxaaPass);

    renderer.setSize(width, height);
    renderer.setPixelRatio(1);
    COMPOSER.render();

    killRenderer(renderer);
    return canvas.toDataURL("image/jpeg", 0.92);
};
